/**
 * CookieConsentManager
 * filepath: resources/assets/front/js/utils/CookieConsentManager.js
 *
 * Traductions 100% dynamiques depuis window.CookieConsentConfig (injecté PHP).
 * Supporte FR, EN, ES, IT, DE — extensible sans toucher ce fichier.
 */

import * as CookieConsent from 'vanilla-cookieconsent';

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector('script[src="' + src + '"]')) return resolve();
        const s = Object.assign(document.createElement('script'), { src, async: true });
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

function detectLang() {
    return (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
}

const CONFIG = {
    get ga4Id()         { return window.CookieConsentConfig?.ga4Id         || null; },
    get matomoUrl()     { return window.CookieConsentConfig?.matomoUrl     || null; },
    get matomoId()      { return window.CookieConsentConfig?.matomoId      || null; },
    get gtmId()         { return window.CookieConsentConfig?.gtmId         || null; },
    get youtubeEnabled(){ return window.CookieConsentConfig?.youtubeEnabled === true; },
    get mapsEnabled()   { return window.CookieConsentConfig?.mapsEnabled   === true; },
    get defaultLang()   { return window.CookieConsentConfig?.defaultLang   || 'fr'; },
    trans(lang) {
        const all = window.CookieConsentConfig?.translations || {};
        return all[lang] || all[CONFIG.defaultLang] || all.fr || all.en || {};
    },
};

export class CookieConsentManager {
    constructor() { this.cc = CookieConsent; }

     init() {
        // Skip total sur les pages utilitaires (login, maintenance, auth/*…)
        // Liste fournie par window.CookieConsentConfig.excludedPaths
        if (this._isExcludedPath()) {
            console.info('[CookieConsentManager] skipped (excluded path:', window.location.pathname, ')');
            return;
        }
 
        this.cc.run(this._buildConfig());
        this._handleConsent();
        this._setupHTMX();
    }

    openPreferences() { this.cc.showPreferences(); }
    openBanner()      { this.cc.show(true); }
    hasConsent(cat)   { return this.cc.acceptedCategory(cat); }

    // ── HTMX detach/reattach ─────────────────────────────────────────────────

    _setupHTMX() {
        let _saved = null;
        document.addEventListener('htmx:beforeSwap', () => {
            const el = document.getElementById('cc-main');
            if (el) { _saved = el; el.remove(); }
        });
        document.addEventListener('htmx:afterSettle', () => {
            if (_saved) { document.body.appendChild(_saved); _saved = null; }
            if (this.cc.acceptedCategory('media')) {
                this._enableYouTube();
                this._enableGoogleMaps();
            }
        });
    }

    // ── Config CookieConsent ─────────────────────────────────────────────────

    _buildConfig() {
        return {
            cookie: { name: 'punky_cookie_consent', expiresAfterDays: 365, sameSite: 'Lax' },
            autoShow: true,
            ui: { theme: 'dark' },
            onFirstConsent: () => this._handleConsent(),
            onConsent:      () => this._handleConsent(),
            onChange: ({ changedCategories }) => {
                if (changedCategories.includes('analytics') && !this.cc.acceptedCategory('analytics')) {
                    this._revokeAnalytics();
                }
                this._handleConsent();
            },
            categories: {
                necessary: { enabled: true, readOnly: true },
                analytics: {
                    enabled: false,
                    autoClear: { cookies: [{ name: /^_ga/ }, { name: /^_gid/ }, { name: /^_gat/ }, { name: /^_pk_/ }] },
                    services: {
                        ...(CONFIG.ga4Id     ? { ga4:    { label: 'Google Analytics 4',  onAccept: () => this._loadGA4(),    onReject: () => {} } } : {}),
                        ...(CONFIG.matomoUrl ? { matomo: { label: 'Matomo Analytics',     onAccept: () => this._loadMatomo(), onReject: () => {} } } : {}),
                        ...(CONFIG.gtmId     ? { gtm:   { label: 'Google Tag Manager',   onAccept: () => this._loadGTM(),   onReject: () => {} } } : {}),
                    },
                },
                media: {
                    enabled: false,
                    autoClear: { cookies: [{ name: /^VISITOR_INFO/ }, { name: /^YSC/ }, { name: /^yt-remote/ }, { name: /^NID/ }] },
                    services: {
                        ...(CONFIG.youtubeEnabled ? { youtube:    { label: 'YouTube',      onAccept: () => this._enableYouTube(),    onReject: () => {} } } : {}),
                        ...(CONFIG.mapsEnabled    ? { googlemaps: { label: 'Google Maps',  onAccept: () => this._enableGoogleMaps(), onReject: () => {} } } : {}),
                    },
                },
            },
            language: {
                default: detectLang(),
                autoDetect: 'document',
                translations: this._buildTranslations(),
            },
        };
    }

    // ── Traductions dynamiques ───────────────────────────────────────────────

    _buildTranslations() {
        const all = window.CookieConsentConfig?.translations || {};
        if (!Object.keys(all).length) return this._fallback();

        const out = {};
        for (const [lang, t] of Object.entries(all)) {
            out[lang] = this._makeT(lang, t);
        }
        return out;
    }

    _makeT(lang, t) {
        const lb  = t.labels || {};
        const pu  = t.privacyUrl  || '#';
        const lu  = t.legalUrl    || '#';
        const cu  = t.contactUrl  || '#';

        const footerParts = [];
        if (lu !== '#') footerParts.push('<a href="' + lu + '">' + (lb.legal   || 'Mentions légales')             + '</a>');
        if (pu !== '#') footerParts.push('<a href="' + pu + '">' + (lb.privacy || 'Politique de confidentialité') + '</a>');

        return {
            consentModal: {
                title:              t.bannerTitle || '🍪 Cookies',
                description:        t.bannerDesc  || '',
                acceptAllBtn:       lb.acceptAll   || 'Tout accepter',
                acceptNecessaryBtn: lb.rejectAll   || 'Refuser tout',
                showPreferencesBtn: lb.managePrefs || 'Gérer mes préférences',
                footer: footerParts.join(' · '),
            },
            preferencesModal: {
                title:               lb.prefsTitle     || 'Paramètres des cookies',
                acceptAllBtn:        lb.acceptAll       || 'Tout accepter',
                acceptNecessaryBtn:  lb.rejectAll       || 'Tout refuser',
                savePreferencesBtn:  lb.savePrefs       || 'Enregistrer',
                closeIconLabel:      lb.close           || 'Fermer',
                serviceCounterLabel: lb.serviceCounter  || 'Service|Services',
                sections: [
                    {
                        title: lb.necessary || 'Cookies nécessaires' + ' <span class="pm__badge">' + (lb.alwaysActive || 'Toujours actifs') + '</span>',
                        description: lb.necessaryDesc || '',
                        linkedCategory: 'necessary',
                    },
                    {
                        title:          lb.analytics    || 'Cookies analytiques',
                        description:    lb.analyticsDesc || '',
                        linkedCategory: 'analytics',
                        ...(this._analyticsTable(lang) ? { cookieTable: this._analyticsTable(lang) } : {}),
                    },
                    // Section médias — masquée si aucun service activé
                    ...(CONFIG.youtubeEnabled || CONFIG.mapsEnabled ? [{
                        title:          lb.media    || 'Cookies médias',
                        description:    lb.mediaDesc || '',
                        linkedCategory: 'media',
                        ...(this._mediaTable(lang) ? { cookieTable: this._mediaTable(lang) } : {}),
                    }] : []),
                    {
                        title: lb.moreInfo || 'Plus d\'informations',
                        // t.moreInfo vient de la BDD (admin) — fallback vide si non renseigné
                        ...(t.moreInfo ? { description: t.moreInfo } : {}),
                    },
                ],
            },
        };
    }

    // ── Tables cookies (statiques, per-lang) ─────────────────────────────────

    _analyticsTable(lang) {
        const H = {
            fr: { cap: 'Liste des cookies', name: 'Cookie', service: 'Service', duration: 'Durée', desc: 'Description' },
            en: { cap: 'Cookie list',       name: 'Cookie', service: 'Service', duration: 'Duration', desc: 'Description' },
            es: { cap: 'Lista de cookies',  name: 'Cookie', service: 'Servicio', duration: 'Duración', desc: 'Descripción' },
            it: { cap: 'Elenco cookie',     name: 'Cookie', service: 'Servizio', duration: 'Durata', desc: 'Descrizione' },
            de: { cap: 'Cookie-Liste',      name: 'Cookie', service: 'Dienst', duration: 'Dauer', desc: 'Beschreibung' },
        };

        // Toutes les lignes possibles, taguées par service
        const ALL = {
            fr: [
                { _svc: 'ga',     name: '_ga',     service: 'Google Analytics', duration: '2 ans',      desc: 'Distingue les utilisateurs uniques.' },
                { _svc: 'ga',     name: '_gid',    service: 'Google Analytics', duration: '24 heures',  desc: 'Identifie la session.' },
                { _svc: 'matomo', name: '_pk_id',  service: 'Matomo',           duration: '13 mois',    desc: 'Identifie l\'utilisateur.' },
                { _svc: 'matomo', name: '_pk_ses', service: 'Matomo',           duration: '30 minutes', desc: 'Identifie la session.' },
            ],
            en: [
                { _svc: 'ga',     name: '_ga',     service: 'Google Analytics', duration: '2 years',    desc: 'Distinguishes unique users.' },
                { _svc: 'ga',     name: '_gid',    service: 'Google Analytics', duration: '24 hours',   desc: 'Identifies the session.' },
                { _svc: 'matomo', name: '_pk_id',  service: 'Matomo',           duration: '13 months',  desc: 'Identifies the user.' },
                { _svc: 'matomo', name: '_pk_ses', service: 'Matomo',           duration: '30 minutes', desc: 'Identifies the session.' },
            ],
            es: [
                { _svc: 'ga',     name: '_ga',     service: 'Google Analytics', duration: '2 años',     desc: 'Distingue usuarios únicos.' },
                { _svc: 'ga',     name: '_gid',    service: 'Google Analytics', duration: '24 horas',   desc: 'Identifica la sesión.' },
                { _svc: 'matomo', name: '_pk_id',  service: 'Matomo',           duration: '13 meses',   desc: 'Identifica al usuario.' },
                { _svc: 'matomo', name: '_pk_ses', service: 'Matomo',           duration: '30 minutos', desc: 'Identifica la sesión.' },
            ],
            it: [
                { _svc: 'ga',     name: '_ga',     service: 'Google Analytics', duration: '2 anni',     desc: 'Distingue gli utenti unici.' },
                { _svc: 'ga',     name: '_gid',    service: 'Google Analytics', duration: '24 ore',     desc: 'Identifica la sessione.' },
                { _svc: 'matomo', name: '_pk_id',  service: 'Matomo',           duration: '13 mesi',    desc: 'Identifica l\'utente.' },
                { _svc: 'matomo', name: '_pk_ses', service: 'Matomo',           duration: '30 minuti',  desc: 'Identifica la sessione.' },
            ],
            de: [
                { _svc: 'ga',     name: '_ga',     service: 'Google Analytics', duration: '2 Jahre',    desc: 'Unterscheidet Nutzer.' },
                { _svc: 'ga',     name: '_gid',    service: 'Google Analytics', duration: '24 Stunden', desc: 'Identifiziert die Sitzung.' },
                { _svc: 'matomo', name: '_pk_id',  service: 'Matomo',           duration: '13 Monate',  desc: 'Identifiziert den Nutzer.' },
                { _svc: 'matomo', name: '_pk_ses', service: 'Matomo',           duration: '30 Minuten', desc: 'Identifiziert die Sitzung.' },
            ],
        };

        // Ne garder que les lignes des services réellement configurés
        const hasGA     = !!CONFIG.ga4Id;
        const hasMatomo = !!CONFIG.matomoUrl;

        const rows = (ALL[lang] || ALL.en)
            .filter(r => (r._svc === 'ga' && hasGA) || (r._svc === 'matomo' && hasMatomo))
            .map(({ _svc, ...r }) => r); // retirer le tag interne

        const h = H[lang] || H.en;

        // Si aucun service configuré → pas de tableau du tout
        if (!rows.length) return null;

        return { caption: h.cap, headers: { name: h.name, service: h.service, duration: h.duration, desc: h.desc }, body: rows };
    }

    _mediaTable(lang) {
        const H = {
            fr: { cap: 'Liste des cookies', dur: 'Durée', desc: 'Description' },
            en: { cap: 'Cookie list',       dur: 'Duration', desc: 'Description' },
            es: { cap: 'Lista de cookies',  dur: 'Duración', desc: 'Descripción' },
            it: { cap: 'Elenco cookie',     dur: 'Durata', desc: 'Descrizione' },
            de: { cap: 'Cookie-Liste',      dur: 'Dauer', desc: 'Beschreibung' },
        };
        const ALL = {
            fr: [
                { _svc: 'youtube', name: 'VISITOR_INFO1_LIVE', service: 'YouTube',      duration: '6 mois',   desc: 'Mesure la bande passante.' },
                { _svc: 'youtube', name: 'YSC',                service: 'YouTube',      duration: 'Session',  desc: 'Enregistre les actions.' },
                { _svc: 'maps',    name: 'NID',                service: 'Google Maps',  duration: '6 mois',   desc: 'Préférences Maps.' },
            ],
            en: [
                { _svc: 'youtube', name: 'VISITOR_INFO1_LIVE', service: 'YouTube',      duration: '6 months', desc: 'Measures bandwidth.' },
                { _svc: 'youtube', name: 'YSC',                service: 'YouTube',      duration: 'Session',  desc: 'Records actions.' },
                { _svc: 'maps',    name: 'NID',                service: 'Google Maps',  duration: '6 months', desc: 'Maps preferences.' },
            ],
            es: [
                { _svc: 'youtube', name: 'VISITOR_INFO1_LIVE', service: 'YouTube',      duration: '6 meses',  desc: 'Mide el ancho de banda.' },
                { _svc: 'youtube', name: 'YSC',                service: 'YouTube',      duration: 'Sesión',   desc: 'Registra acciones.' },
                { _svc: 'maps',    name: 'NID',                service: 'Google Maps',  duration: '6 meses',  desc: 'Preferencias Maps.' },
            ],
            it: [
                { _svc: 'youtube', name: 'VISITOR_INFO1_LIVE', service: 'YouTube',      duration: '6 mesi',   desc: 'Misura la larghezza di banda.' },
                { _svc: 'youtube', name: 'YSC',                service: 'YouTube',      duration: 'Sessione', desc: 'Registra le azioni.' },
                { _svc: 'maps',    name: 'NID',                service: 'Google Maps',  duration: '6 mesi',   desc: 'Preferenze Maps.' },
            ],
            de: [
                { _svc: 'youtube', name: 'VISITOR_INFO1_LIVE', service: 'YouTube',      duration: '6 Monate', desc: 'Misst die Bandbreite.' },
                { _svc: 'youtube', name: 'YSC',                service: 'YouTube',      duration: 'Sitzung',  desc: 'Zeichnet Aktionen auf.' },
                { _svc: 'maps',    name: 'NID',                service: 'Google Maps',  duration: '6 Monate', desc: 'Maps-Einstellungen.' },
            ],
        };

        const rows = (ALL[lang] || ALL.en)
            .filter(r =>
                (r._svc === 'youtube' && CONFIG.youtubeEnabled) ||
                (r._svc === 'maps'    && CONFIG.mapsEnabled)
            )
            .map(({ _svc, ...r }) => r);

        const h = H[lang] || H.en;

        if (!rows.length) return null;

        return { caption: h.cap, headers: { name: 'Cookie', service: 'Service', duration: h.dur, desc: h.desc }, body: rows };
    }

    _fallback() {
        return {
            fr: this._makeT('fr', {
                bannerTitle: '🍪 Nous utilisons des cookies', bannerDesc: '',
                privacyUrl: '#', legalUrl: '#', contactUrl: '#',
                labels: { acceptAll: 'Tout accepter', rejectAll: 'Refuser tout', managePrefs: 'Gérer mes préférences',
                          savePrefs: 'Enregistrer', close: 'Fermer', prefsTitle: 'Paramètres des cookies',
                          necessary: 'Cookies nécessaires', analytics: 'Cookies analytiques', media: 'Cookies médias',
                          moreInfo: 'Plus d\'informations', alwaysActive: 'Toujours actifs', serviceCounter: 'Service|Services' },
            }),
            en: this._makeT('en', {
                bannerTitle: '🍪 We use cookies', bannerDesc: '',
                privacyUrl: '#', legalUrl: '#', contactUrl: '#',
                labels: { acceptAll: 'Accept all', rejectAll: 'Reject all', managePrefs: 'Manage preferences',
                          savePrefs: 'Save preferences', close: 'Close', prefsTitle: 'Cookie settings',
                          necessary: 'Necessary cookies', analytics: 'Analytics cookies', media: 'Media cookies',
                          moreInfo: 'More information', alwaysActive: 'Always active', serviceCounter: 'Service|Services' },
            }),
        };
    }

    // ── Services tiers ───────────────────────────────────────────────────────

    _handleConsent() {
        if (this.cc.acceptedCategory('analytics')) {
            if (CONFIG.ga4Id)     this._loadGA4();
            if (CONFIG.matomoUrl) this._loadMatomo();
            if (CONFIG.gtmId)     this._loadGTM();
        }
        if (this.cc.acceptedCategory('media')) {
            this._enableYouTube();
            this._enableGoogleMaps();
        }
    }

    _loadGA4() {
        if (!CONFIG.ga4Id || window._ga4Loaded) return;
        window._ga4Loaded = true;
        loadScript('https://www.googletagmanager.com/gtag/js?id=' + CONFIG.ga4Id).then(() => {
            window.dataLayer = window.dataLayer || [];
            window.gtag = function() { window.dataLayer.push(arguments); };
            window.gtag('js', new Date());
            window.gtag('config', CONFIG.ga4Id, { anonymize_ip: true });
        });
    }

    _loadMatomo() {
        if (!CONFIG.matomoUrl || !CONFIG.matomoId || window._matomoLoaded) return;
        window._matomoLoaded = true;
        window._paq = window._paq || [];
        window._paq.push(['trackPageView']);
        window._paq.push(['enableLinkTracking']);
        const u = CONFIG.matomoUrl.replace(/\/?$/, '/');
        window._paq.push(['setTrackerUrl', u + 'matomo.php']);
        window._paq.push(['setSiteId', String(CONFIG.matomoId)]);
        loadScript(u + 'matomo.js');
    }

    _loadGTM() {
        if (!CONFIG.gtmId || window._gtmLoaded) return;
        window._gtmLoaded = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        loadScript('https://www.googletagmanager.com/gtm.js?id=' + CONFIG.gtmId);
    }

    _enableYouTube() {
        document.querySelectorAll('[data-youtube-id]:not([data-yt-ready])').forEach(el => {
            el.dataset.ytReady = '1';
            const iframe = Object.assign(document.createElement('iframe'), {
                src: 'https://www.youtube-nocookie.com/embed/' + el.dataset.youtubeId,
                loading: 'lazy',
                allowFullscreen: true,
                className: el.dataset.iframeClass || '',
            });
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
            el.replaceWith(iframe);
        });
        document.querySelectorAll('iframe[data-src*="youtube"]').forEach(f => {
            if (!f.src || f.src === 'about:blank') f.src = f.dataset.src;
        });
    }

    _enableGoogleMaps() {
        document.querySelectorAll('[data-maps-src]:not([data-maps-ready])').forEach(el => {
            el.dataset.mapsReady = '1';
            const iframe = Object.assign(document.createElement('iframe'), {
                src: el.dataset.mapsSrc,
                loading: 'lazy',
                allowFullscreen: true,
                className: el.dataset.iframeClass || '',
            });
            el.replaceWith(iframe);
        });
        document.querySelectorAll('iframe[data-src*="maps.google"]').forEach(f => {
            if (!f.src || f.src === 'about:blank') f.src = f.dataset.src;
        });
    }

    _revokeAnalytics() {
        if (CONFIG.ga4Id)   window['ga-disable-' + CONFIG.ga4Id] = true;
        if (window._paq)    window._paq.push(['optUserOut']);
        window._ga4Loaded = window._matomoLoaded = window._gtmLoaded = false;
    }

    //
    // Ajoute cette méthode dans la classe (n'importe où, par exemple juste
    // après init() ou dans la section "── HTMX detach/reattach ──") :
 
 
    /**
     * Le path actuel match-il un des patterns d'exclusion configurés ?
     * Patterns wildcard `*` supportés (ex: /auth/*).
     */
    _isExcludedPath() {
        const paths = window.CookieConsentConfig?.excludedPaths || [];
        if (!Array.isArray(paths) || paths.length === 0) return false;
 
        const current = window.location.pathname;
        return paths.some(pattern => this._matchPathPattern(current, pattern));
    }
 
    /**
     * Match wildcard pattern (* = zéro ou plus de caractères, regex-safe).
     */
    _matchPathPattern(path, pattern) {
        // Normalisation du leading slash
        if (pattern.startsWith('/')) {
            path = '/' + path.replace(/^\/+/, '');
        }
 
        // Échappe tous les caractères regex sauf *
        const regex = new RegExp(
            '^' + pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$',
            'i'
        );
 
        return regex.test(path);
    }
 
}

export default new CookieConsentManager();